/**
 * PrestadorCardBonda: Tarjeta de prestador con imagen grande de fondo +
 * logo circular flotante al centro (estilo Bonda / Comunidad ATE).
 * Se usa en los carruseles de Novedades / Destacados / Los más usados
 * y en el grid de "Descubrí más beneficios" del detalle del prestador.
 */
import React from 'react';
import { Gift, Package } from 'lucide-react';
import { Badge } from '../ui/badge';

export default function PrestadorCardBonda({
  prestador,
  categoriaIcon,
  onClick,
  badge,
  badgeClass = 'bg-purple-600',
  showCounter = false,
  className = '',
  testId,
}) {
  const Icon = categoriaIcon || Gift;
  const heroImg =
    prestador.imagen_principal ||
    (prestador.imagenes && prestador.imagenes[0]) ||
    null;

  return (
    <button
      onClick={onClick}
      className={`group w-full bg-white rounded-2xl border border-gray-200 hover:border-purple-400 hover:shadow-lg transition-all text-left overflow-hidden ${className}`}
      data-testid={testId || `prestador-card-${prestador.id}`}
    >
      {/* Imagen de fondo con overlay + logo circular flotante */}
      <div className="relative aspect-[4/3] bg-gradient-to-br from-purple-100 via-purple-50 to-white overflow-hidden">
        {heroImg ? (
          <>
            <img
              src={heroImg}
              alt={prestador.nombre}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="w-16 h-16 text-purple-300" />
          </div>
        )}
        {badge && (
          <Badge className={`absolute top-2 left-2 text-white text-[10px] shadow ${badgeClass}`}>
            {badge}
          </Badge>
        )}
        {showCounter && prestador.vouchers_generados && (
          <span className="absolute top-2 right-2 bg-white/95 text-emerald-700 text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
            {prestador.vouchers_generados} usos
          </span>
        )}
        {/* Logo circular flotante */}
        <div className="absolute inset-x-0 bottom-0 flex justify-center translate-y-1/2">
          <div className="w-16 h-16 bg-white rounded-2xl shadow-md ring-4 ring-white flex items-center justify-center overflow-hidden">
            {prestador.logo_url ? (
              <img
                src={prestador.logo_url}
                alt={prestador.nombre}
                className="max-w-full max-h-full object-contain p-1.5"
              />
            ) : (
              <Icon className="w-8 h-8 text-purple-400" />
            )}
          </div>
        </div>
      </div>
      <div className="pt-10 pb-4 px-3 text-center">
        <p className="font-bold text-gray-900 text-sm uppercase tracking-tight line-clamp-1">
          {prestador.nombre}
        </p>
        <p className="text-xs text-purple-700 mt-1 line-clamp-2 leading-snug min-h-[2rem]">
          {prestador.beneficio}
        </p>
        {Array.isArray(prestador.paquetes) && prestador.paquetes.length > 0 && (
          <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-sky-800 bg-sky-100 border border-sky-200 px-2 py-0.5 rounded-full">
            <Package className="w-3 h-3" />
            {prestador.paquetes.length} {prestador.paquetes.length === 1 ? 'paquete' : 'paquetes'}
          </div>
        )}
      </div>
    </button>
  );
}
